import { Extension, Mark, mergeAttributes, type Editor } from '@tiptap/core';

export const TEXT_ALIGNMENTS = ['left', 'center', 'right'] as const;
export type TextAlignment = (typeof TEXT_ALIGNMENTS)[number];

export const DEFAULT_TEXT_ALIGNMENT: TextAlignment = 'left';

/** Nodes that carry an alignment. Blocks with their own layout (images, grids,
 *  callouts, spacers) are deliberately excluded. */
const ALIGNABLE_TYPES = ['paragraph', 'heading'];

function normalizeAlignment(value: unknown): TextAlignment {
    return (TEXT_ALIGNMENTS as readonly string[]).includes(value as string)
        ? (value as TextAlignment)
        : DEFAULT_TEXT_ALIGNMENT;
}

/**
 * Local implementation rather than `@tiptap/extension-text-align`: the consuming
 * apps pin `@tiptap/core` in their bundler's `dedupe` list, so an extension
 * installed under this package would be linked against their older core and
 * fail to resolve its imports. Same reason for RichHighlight below.
 *
 * Registered in the schema unconditionally so stored documents render read-only
 * everywhere; only the toolbar affordance is gated
 * (RichEditorProps.allowTextStyling).
 */
export const RichTextAlign = Extension.create({
    name: 'richTextAlign',

    addGlobalAttributes() {
        return [
            {
                types: ALIGNABLE_TYPES,
                attributes: {
                    textAlign: {
                        default: DEFAULT_TEXT_ALIGNMENT,
                        parseHTML: (element: HTMLElement) =>
                            normalizeAlignment(element.style.textAlign),
                        renderHTML: (attributes: Record<string, unknown>) => {
                            const alignment = normalizeAlignment(attributes.textAlign);
                            return alignment === DEFAULT_TEXT_ALIGNMENT
                                ? {}
                                : { style: `text-align: ${alignment}` };
                        }
                    }
                }
            }
        ];
    }
});

/** Mirrors `@tiptap/extension-text-align`'s own `setTextAlign`: update whichever
 *  alignable type the selection actually sits in. */
export function applyTextAlign(editor: Editor, alignment: TextAlignment): void {
    const chain = editor.chain().focus();
    ALIGNABLE_TYPES.forEach((type) => chain.updateAttributes(type, { textAlign: alignment }));
    chain.run();
}

// Styled through a class rather than an app stylesheet - the rich-editor CSS is
// duplicated across apps this package does not own.
export const HIGHLIGHT_CLASS = 'rounded-[3px] bg-yellow-200 px-0.5 py-px text-inherit';

export const RichHighlight = Mark.create({
    name: 'highlight',

    parseHTML() {
        return [{ tag: 'mark' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['mark', mergeAttributes(HTMLAttributes, { class: HIGHLIGHT_CLASS }), 0];
    }
});
